// @ts-nocheck
// NEW DESIGN - Button-based switcher with transparent/solid panel style
// To revert: copy contents from DashboardsPageClient.backup.tsx
"use client";

import { Button } from "@/components/ui/button";
import { Footer } from "@/app/components/Footer";
import { ReactNode, useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { LayoutDashboard, Database } from "lucide-react";

const sections = [
  {
    id: "dashboards",
    label: "Dashboards",
    icon: LayoutDashboard,
  },
  {
    id: "data",
    label: "Data Sources",
    icon: Database,
  },
];

export default function DashboardsPageClient({ 
  children, 
  isSubscribed 
}: { 
  children: ReactNode[]; 
  isSubscribed: boolean;
}) {
  const searchParams = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeSection, setActiveSection] = useState(tabParam || "dashboards");

  useEffect(() => {
    if (tabParam && ["dashboards", "data"].includes(tabParam)) {
      setActiveSection(tabParam);
    }
  }, [tabParam]);

  const childrenArray = Array.isArray(children) ? children : [children];

  return (
    <>
      {/* Full viewport container with gradient background */}
      <div className="fixed inset-0 pt-[5vh] overflow-hidden">
        {/* Gradient backdrop */}
        <div className="absolute inset-0 app-gradient-bg" />
        
        {/* Content wrapper - centered with padding */}
        <div className="relative h-full w-full flex flex-col items-center px-4 sm:px-8 lg:px-12 py-8 overflow-hidden">
          
          {/* Main content panel - frosted glass effect */}
          <div className="w-full max-w-7xl flex-1 flex flex-col min-h-0 rounded-2xl bg-background/70 backdrop-blur-xl shadow-xl overflow-hidden">
            
            {/* Inner solid panel */}
            <div className="flex-1 m-4 sm:m-6 rounded-xl bg-card shadow-sm flex flex-col min-h-0 overflow-hidden">
              
              {/* Header with section buttons */}
              <div className="px-6 py-5 border-b border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground">
                    {isSubscribed ? "Your Workspace" : "Explore"}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isSubscribed 
                      ? "Access your dashboards and data sources" 
                      : "Subscribe to unlock full access"}
                  </p>
                </div>
                
                {/* Section toggle buttons */}
                <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    const isActive = activeSection === section.id;
                    return (
                      <Button
                        key={section.id}
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setActiveSection(section.id)}
                        className={`gap-2 transition-all ${
                          isActive 
                            ? "shadow-sm" 
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="hidden sm:inline">{section.label}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto p-6">
                {activeSection === "dashboards" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {childrenArray[0]}
                  </div>
                )}

                {activeSection === "data" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {childrenArray[1]}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

